import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';
import { Breakpoint } from '@mui/system';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, BannerStatus, Button, ButtonProps, HSpacer, Text } from '..';

export interface FullscreenDialogProps {
  actionButton?: (props: ButtonProps) => ReactNode,
  bannerStatus?: BannerStatus,
  bannerText?: string,
  children?: ReactNode,
  contentStyle?: SxProps<Theme>,
  dialogActions?: ReactNode,
  hideCloseButton?: boolean,
  hideHeader?: boolean,
  hideTitleBorder?: boolean,
  hideScrollbar?: boolean,
  maxWidth?: Breakpoint,
  onClose?: () => void,
  open?: boolean,
  sx?: SxProps<Theme>,
  testID: string,
  title?: string | ReactNode,
  titleComponent?: ReactNode,
  titleDescriptor?: ReactNode,
  titleStyle?: SxProps<Theme>,
}

export const FullscreenDialog = ({
  actionButton,
  bannerStatus = 'info',
  bannerText,
  children,
  contentStyle,
  dialogActions,
  hideCloseButton,
  hideHeader = false,
  hideTitleBorder = false,
  hideScrollbar = false,
  maxWidth = 'xl',
  onClose,
  open = false,
  sx,
  testID,
  title,
  titleComponent,
  titleDescriptor,
  titleStyle,
}: FullscreenDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const ActionButtonProps: ButtonProps = {
    color: 'secondary',
    sx: { borderColor: theme.palette.outline },
    testID: `${testID}-accept`,
    variant: 'outlined',
  };

  return (
    <Dialog
      PaperProps={{
        style: {
          alignItems: 'center',
          backgroundColor: theme.palette.background.default,
          backgroundImage: 'none',
          borderRadius: 0,
          paddingLeft: '4px',
          paddingRight: '4px',
        },
      }}
      data-testid={testID}
      fullScreen={open}
      onClose={onClose}
      open={open}
      scroll="paper"
      sx={sx}
      transitionDuration={0.01}
    >
      {bannerText && (
        <div style={{
          margin: '24px 0 auto',
          maxWidth: theme.breakpoints.values[maxWidth],
          width: '100%',
          paddingLeft: '24px',
          paddingRight: '24px',
        }}>
          <Banner
            status={bannerStatus}
            title={bannerText}
          />
        </div>
      )}
      {!hideHeader && (
        <DialogTitle sx={{
          borderBottom: hideTitleBorder ? undefined : '1px solid rgba(0, 0, 0, 0.04)',
          margin: '0 auto',
          padding: 0,
          width: '100%',
          ...titleStyle,
        }}>
          <Stack
            alignItems="center"
            data-testid={`${testID}-header`}
            direction="row"
            justifyContent="space-between"
            sx={{
              minHeight: "48px",
              margin: '0 auto',
              maxWidth: theme.breakpoints.values[maxWidth],
              padding: '8px 24px',
            }}
          >
            <Stack alignItems="center" direction="row">
              {!!titleComponent && titleComponent}
              {!!titleComponent && !!title && (
                <HSpacer size="5" />
              )}
              {!!title && (
                <>
                  {typeof title === 'string'
                    ? <Text category="headline-small">{title}</Text>
                    : title
                  }
                </>
              )}
              {!!titleDescriptor && (
                <>
                  <HSpacer size="4" />
                  <HSpacer size="5" />
                  {titleDescriptor}
                </>
              )}
            </Stack>
            {(!!actionButton || (!hideCloseButton && !!onClose)) && (
              <Stack direction="row">
                {!!actionButton && actionButton(ActionButtonProps)}
                {!!actionButton && !hideCloseButton && !!onClose && (
                  <HSpacer size="5" />
                )}
                {!hideCloseButton && !!onClose && (
                  <Button
                    color="secondary"
                    onClick={onClose}
                    startIcon={<CloseIcon />}
                    testID={`${testID}-close`}
                  >
                    {t($ => $.common.actions.close)}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          margin: '0 auto',
          padding: '0 24px',
          maxWidth: theme.breakpoints.values[maxWidth],
          width: '100%',
          ...(hideScrollbar ? {
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          } : {}),
          ...contentStyle,
        }}
      >
        {children}
      </DialogContent>
      {!!dialogActions && (
        <DialogActions sx={{ p: 0, width: '100%' }}>
          {dialogActions}
        </DialogActions>)
      }
    </Dialog>
  );
};
